import { reduce, isNil } from 'lodash';
import * as History from 'history';

import { GetUrlParams } from '@tradingblock/api';
import { TradingBlockEnvironments } from '@tradingblock/types';
import { VirtualTradingPaths, config as Config, ConfigType } from './clientConfig';

export const toHashParams = (args: Record<string, any>) => {
  const combinedValues = reduce(
    args,
    (acc: string[], v: any, k: string) => {
      if (isNil(v)) {
        return acc;
      }
      return [...acc, `${k}=${v}`];
    },
    []
  ).join('&');
  return `#${combinedValues}`;
};

export const getEnvironment = (location: History.Location<any>): string => {
  const params = GetUrlParams(location.search);
  const environment = params['environment'] || undefined;

  if (environment) {
    return environment;
  } else if (
    Config.environment === TradingBlockEnvironments.virtual ||
    VirtualTradingPaths.includes(location.pathname)
  ) {
    return 'virtual';
  } else {
    return 'dashboard';
  }
};

export const getConfig = (env: string): ConfigType => {
  if (env === 'virtual') {
    return Config.virtual;
  }
  return Config.dashboard;
};

/**
 * Gets the subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 * Works both server-side (from config) and client-side (from window variable).
 * @param configSubdirectory - Optional subdirectory from config (server-side).
 * @returns The subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 */
export const getSubdirectory = (configSubdirectory?: string): string => {
  // Try provided config value first (works server-side).
  if (configSubdirectory) {
    return configSubdirectory;
  }

  // Try Config.subdirectory (works server-side).
  if (Config.subdirectory) {
    return Config.subdirectory;
  }

  // Fall back to window variable (client-side).
  if (typeof window !== 'undefined' && (window as any).__TB_SUBDIRECTORY__) {
    return (window as any).__TB_SUBDIRECTORY__;
  }

  return '';
};

/**
 * Gets a static asset path (images, fonts, CSS, JS, etc.) with subdirectory prefix.
 * Handles absolute URLs (http://, https://) by returning them as-is.
 * @param path - The asset path (e.g., '/images/logo.png' or 'images/logo.png').
 * @param subdirectoryOverride - Optional subdirectory to use (for server-side calls).
 * @returns The path with subdirectory prefix if configured.
 */
export const getStaticPath = (path: string, subdirectoryOverride?: string): string => {
  // If path is already an absolute URL, return as-is.
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Ensure path starts with /.
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Use provided subdirectory override (server-side) or get from config/window (client-side).
  let subdirectory = subdirectoryOverride !== undefined ? subdirectoryOverride : getSubdirectory();

  // Remove trailing slash from subdirectory if present.
  const cleanSubdirectory = subdirectory.endsWith('/') ? subdirectory.slice(0, -1) : subdirectory;

  return cleanSubdirectory ? `${cleanSubdirectory}${normalizedPath}` : normalizedPath;
};

/**
 * Prepends subdirectory prefix to an internal path when used as a query parameter in external URLs.
 * This is specifically for redirect URLs where internal paths (e.g., '/', '/status') are passed
 * as query parameters to external URLs (e.g., 'https://dashboard.tradingblock.com?next=/')
 * @param path - The internal path (e.g., '/' or '/status' or '/home').
 * @returns The path with subdirectory prefix if configured (e.g., '/TBFE-1955-2/' or '/').
 */
export const getPathForRedirect = (path: string): string => {
  return getStaticPath(path);
};
